import * as React from 'react';
import { cn } from '../../lib/utils';

interface DialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  children: React.ReactNode;
}

export const Dialog = ({ open, onOpenChange, children }: DialogProps) => {
  React.useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.body.style.overflow = '';
    };
  }, [open]);

  React.useEffect(() => {
    if (!open) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onOpenChange(false);
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [onOpenChange, open]);

  if (!open) {
    return null;
  }

  return (
    <div
      className='fixed inset-0 z-50 flex items-center justify-center'
      onClick={() => onOpenChange(false)}
    >
      {/* Backdrop */}
      <div className='absolute inset-0 bg-black/50' />

      {/* Dialog content */}
      <div className='relative z-10' onClick={(e) => e.stopPropagation()}>
        {children}
      </div>
    </div>
  );
};

interface DialogContentProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
}

export const DialogContent = ({ className, children, ...props }: DialogContentProps) => {
  return (
    <div
      className={cn('relative bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4', className)}
      {...props}
    >
      {children}
    </div>
  );
};

interface DialogHeaderProps {
  children: React.ReactNode;
}

export const DialogHeader = ({ children }: DialogHeaderProps) => {
  return <div className='mb-4'>{children}</div>;
};

interface DialogTitleProps {
  children: React.ReactNode;
}

export const DialogTitle = ({ children }: DialogTitleProps) => {
  return <h2 className='text-xl font-semibold text-gray-900'>{children}</h2>;
};

interface DialogDescriptionProps {
  children: React.ReactNode;
}

export const DialogDescription = ({ children }: DialogDescriptionProps) => {
  return <p className='text-sm text-gray-600 mt-2'>{children}</p>;
};

interface DialogFooterProps {
  children: React.ReactNode;
}

export const DialogFooter = ({ children }: DialogFooterProps) => {
  return <div className='flex justify-end gap-3 mt-6'>{children}</div>;
};
